import { inject, Injectable, NgZone, OnDestroy } from '@angular/core';
import * as signalR from '@microsoft/signalr';
import { Subject } from 'rxjs';
import { ENVIRONMENT_TOKEN } from '../injection-tokens/environment.token';
import { UserSettings } from '../models/setting.model';
import { AuthStore } from '../../store/auth.store';

export type SignalRConnectionState = 'connected' | 'disconnected' | 'reconnecting' | 'partial';

/**
 * Reconnect backoff for the settings hub: retry immediately on the first attempt,
 * then escalate (2s → 10s → 30s) and keep retrying INDEFINITELY. The previous fixed
 * schedule gave up after ~30s and closed the connection, silently dropping real-time
 * updates until a full page reload. This never returns null (never gives up); each
 * successful reconnect fires reconnected$ so subscribers reconcile via REST.
 *
 * Exported as a pure function so the backoff curve can be unit-tested without a live hub.
 */
export function settingsReconnectDelay(retryContext: signalR.RetryContext): number {
  if (retryContext.previousRetryCount === 0) return 0; // retry immediately first
  if (retryContext.elapsedMilliseconds < 60_000) return 2_000;
  if (retryContext.elapsedMilliseconds < 300_000) return 10_000;
  return 30_000; // steady-state: one attempt every 30s, forever
}

/**
 * SignalR service for receiving real-time user updates from the backend.
 *
 * Usage:
 * 1. Inject the service
 * 2. Call startConnection(userIdentityId) after authentication
 * 3. Subscribe to settingsUpdated$ to receive updates
 * 4. Call stopConnection() when done (e.g., on logout)
 *
 * Connection States:
 * - 'connected': Fully connected and subscribed to user group
 * - 'disconnected': Not connected to SignalR hub
 * - 'reconnecting': Attempting to reconnect after connection loss
 * - 'partial': Connected to hub but failed to join user group (won't receive updates)
 */
@Injectable({ providedIn: 'root' })
export class SignalRService implements OnDestroy {
  private readonly environment = inject(ENVIRONMENT_TOKEN);
  private readonly zone = inject(NgZone);
  private readonly authStore = inject(AuthStore);

  private hubConnection: signalR.HubConnection | null = null;
  private currentUserIdentityId: string | null = null;
  private isConnecting = false;
  private readonly maxJoinGroupRetries = 3;
  private readonly joinGroupRetryDelayMs = 1000;

  /**
   * Observable that emits when settings are updated for the current user.
   * Subscribe to this in components that need to react to settings changes.
   */
  public readonly settingsUpdated$ = new Subject<UserSettings>();

  /**
   * Emits when the programmes available to the current user change.
   * Subscribers must reconcile the current task through the REST API.
   */
  public readonly userProgramsChanged$ = new Subject<void>();

  /**
   * Emits the id of a test that was removed from the current user.
   */
  public readonly userTestRemoved$ = new Subject<number>();

  /**
   * Observable that emits connection state changes.
   * 'partial' indicates connected but not subscribed to notifications.
   */
  public readonly connectionState$ = new Subject<SignalRConnectionState>();

  /**
   * Emits after a successful reconnect (and group rejoin) following a connection drop.
   *
   * Subscribers should treat this as a signal to refetch any state that may
   * have changed during the disconnection window. SignalR delivery is
   * fire-and-forget (the Azure SignalR Service does not buffer messages for
   * disconnected clients), so any pushes during the gap are lost - reconciling
   * via REST is the safety net.
   *
   * Does NOT emit on the initial connect; only on subsequent reconnects.
   */
  public readonly reconnected$ = new Subject<void>();

  /**
   * Starts the SignalR connection and joins the user's notification group.
   * @param userIdentityId The user's identity ID (from Account.identityId)
   */
  async startConnection(userIdentityId: string): Promise<void> {
    if (this.isConnecting) {
      this.debug('[SignalR] Connection already in progress');
      return;
    }

    if (this.hubConnection?.state === signalR.HubConnectionState.Connected) {
      if (this.currentUserIdentityId === userIdentityId) {
        this.debug('[SignalR] Already connected for this user');
        return;
      }
      // Different user - disconnect and reconnect
      await this.stopConnection();
    }

    this.isConnecting = true;
    this.currentUserIdentityId = userIdentityId;

    try {
      this.hubConnection = new signalR.HubConnectionBuilder()
        .withUrl(`${this.environment.baseApiUrl}/hubs/settings`, {
          withCredentials: true,
          // The API authenticates with JWT Bearer. WebSockets cannot send an
          // Authorization header, so the access token is supplied here; SignalR
          // forwards it as the `access_token` query parameter on the negotiate
          // request, which the backend reads for /hubs paths. Without an
          // authenticated connection the hub cannot bind the user to their own
          // notification group, so this is required for delivery.
          accessTokenFactory: () => this.authStore.authInfo().accessToken ?? '',
        })
        // Indefinite, capped reconnect backoff (see settingsReconnectDelay).
        .withAutomaticReconnect({ nextRetryDelayInMilliseconds: settingsReconnectDelay })
        .configureLogging(signalR.LogLevel.Information)
        .build();

      this.setupEventHandlers();

      await this.hubConnection.start();
      this.debug('[SignalR] Connected successfully');

      // Join the user's notification group with retry
      const joined = await this.joinUserGroupWithRetry(userIdentityId);
      this.connectionState$.next(joined ? 'connected' : 'partial');
    } catch (error) {
      console.error('[SignalR] Connection failed:', error);
      this.connectionState$.next('disconnected');
      throw error;
    } finally {
      this.isConnecting = false;
    }
  }

  /**
   * Attempts to join the user's notification group with retry logic.
   * @returns true if successfully joined, false otherwise
   */
  private async joinUserGroupWithRetry(userIdentityId: string): Promise<boolean> {
    for (let attempt = 1; attempt <= this.maxJoinGroupRetries; attempt++) {
      try {
        if (
          !this.hubConnection ||
          this.hubConnection.state !== signalR.HubConnectionState.Connected
        ) {
          console.warn('[SignalR] Cannot join group - not connected');
          return false;
        }

        await this.hubConnection.invoke('JoinUserGroup', userIdentityId);
        this.debug(`[SignalR] Joined user group: ${userIdentityId}`);
        return true;
      } catch (error) {
        console.error(
          `[SignalR] Failed to join group (attempt ${attempt}/${this.maxJoinGroupRetries}):`,
          error
        );

        if (attempt < this.maxJoinGroupRetries) {
          const delay = this.joinGroupRetryDelayMs * attempt; // Linear backoff
          this.debug(`[SignalR] Retrying in ${delay}ms...`);
          await this.delay(delay);
        }
      }
    }

    console.error('[SignalR] Exhausted all retries for joining user group');
    return false;
  }

  private delay(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  /** Verbose, informational logging - suppressed in production builds to avoid console noise. */
  private debug(...args: unknown[]): void {
    if (!this.environment.production) {
      console.log(...args);
    }
  }

  /**
   * Stops the SignalR connection and cleans up resources.
   */
  async stopConnection(): Promise<void> {
    if (this.hubConnection) {
      try {
        await this.hubConnection.stop();
        this.debug('[SignalR] Disconnected');
      } catch (error) {
        console.error('[SignalR] Error during disconnect:', error);
      } finally {
        this.hubConnection = null;
        this.currentUserIdentityId = null;
        this.connectionState$.next('disconnected');
      }
    }
  }

  /**
   * Returns true if currently connected to the SignalR hub.
   */
  isConnected(): boolean {
    return this.hubConnection?.state === signalR.HubConnectionState.Connected;
  }

  ngOnDestroy(): void {
    this.stopConnection();
    this.settingsUpdated$.complete();
    this.userProgramsChanged$.complete();
    this.userTestRemoved$.complete();
    this.connectionState$.complete();
    this.reconnected$.complete();
  }

  private setupEventHandlers(): void {
    if (!this.hubConnection) return;

    // Listen for settings updates from the backend
    // This must match the method name in your ISettingsHubClient interface in the backend
    // The backend's ISettingsNotificationService calls this method via HubContext
    this.hubConnection.on('SettingsUpdated', (settings: UserSettings) => {
      // Run inside Angular zone to trigger change detection
      this.zone.run(() => {
        this.debug('[SignalR] Received settings update:', settings);
        this.settingsUpdated$.next(settings);
      });
    });

    this.hubConnection.on('UserProgramsChanged', () => {
      this.zone.run(() => {
        this.debug('[SignalR] Received user programmes change');
        this.userProgramsChanged$.next();
      });
    });

    this.hubConnection.on('UserTestRemoved', (userTestId: number) => {
      this.zone.run(() => {
        this.debug('[SignalR] Received user test removal:', userTestId);
        this.userTestRemoved$.next(userTestId);
      });
    });

    // Handle reconnection events
    this.hubConnection.onreconnecting((error?: Error) => {
      console.warn('[SignalR] Reconnecting...', error);
      this.connectionState$.next('reconnecting');
    });

    this.hubConnection.onreconnected(async (connectionId?: string) => {
      this.debug('[SignalR] Reconnected with ID:', connectionId);

      // Re-join user group after reconnection with retry
      if (this.currentUserIdentityId) {
        const joined = await this.joinUserGroupWithRetry(this.currentUserIdentityId);
        this.connectionState$.next(joined ? 'connected' : 'partial');
        if (joined) {
          // Notify subscribers that they should reconcile any state that may
          // have changed during the outage. SignalR delivery is fire-and-forget
          // (the Azure SignalR Service does not buffer messages for disconnected
          // clients), so pushes that happened during the gap are lost. Run inside
          // Angular zone to trigger change detection in subscribers.
          this.zone.run(() => this.reconnected$.next());
        }
      } else {
        this.connectionState$.next('connected');
      }
    });

    this.hubConnection.onclose((error?: Error) => {
      this.debug('[SignalR] Connection closed', error);
      this.connectionState$.next('disconnected');
    });
  }
}
